import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ArrowUpRight, User, LogOut } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useT } from "../lib/i18n";
import { useAuth } from "../lib/AuthContext";

const KEYS = [
  { key: "about", label: "nav.about", to: "about" },
  { key: "trainings", label: "nav.trainings", to: "schedule" },
  { key: "schedule", label: "nav.schedule", to: "schedule" },
  { key: "membership", label: "nav.membership", to: "membership" },
  { key: "pt", label: "nav.pt", to: "pt" },
  { key: "community", label: "nav.community", to: "community" },
  { key: "contact", label: "nav.contact", to: "contact" },
];

function LangToggle({ dark = false }) {
  const { lang, setLang } = useT();
  const active = dark ? "text-[#F5F3F0]" : "text-[#0A0A0A]";
  const idle = dark ? "text-[#A3A3A3]" : "text-[#9A968E]";
  return (
    <div className="flex items-center gap-1 chip" data-testid="lang-toggle">
      <button onClick={() => setLang("nl")} className={lang === "nl" ? active : idle} data-testid="lang-nl">NL</button>
      <span className={idle}>/</span>
      <button onClick={() => setLang("en")} className={lang === "en" ? active : idle} data-testid="lang-en">EN</button>
    </div>
  );
}

export default function Nav() {
  const { t } = useT();
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 ${scrolled ? "backdrop-blur-md bg-[#F5F3F0]/80 line-b" : ""}`}
        data-testid="site-nav"
      >
        <div className="flex items-center justify-between px-6 md:px-10 py-5">
          <button
            onClick={() => scrollTo("hero")}
            className="font-logo text-2xl md:text-3xl text-[#0A0A0A] accent-hover"
            data-testid="brand-logo"
          >
            JOH<span className="text-[#4A4A4A]">.</span>
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {KEYS.map((n) => (
              <button
                key={n.key}
                onClick={() => scrollTo(n.to)}
                className="chip accent-hover"
                data-testid={`nav-${n.key}`}
              >
                {t(n.label)}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <div className="hidden md:block lg:ml-10"><LangToggle /></div>
            {user && user !== false ? (
              <>
                <Link to="/dashboard" className="hidden md:inline-flex chip text-[#0A0A0A] accent-hover items-center gap-1" data-testid="nav-dashboard-link">
                  <User size={14}/> Dashboard
                </Link>
                <button
                  onClick={() => { signOut(); }}
                  className="hidden md:inline-flex chip text-[#4A4A4A] accent-hover items-center gap-1"
                  data-testid="nav-sign-out"
                  title="Uitloggen"
                >
                  <LogOut size={14}/>
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="hidden md:inline-flex chip-login text-[#0A0A0A] accent-hover items-center gap-1"
                data-testid="nav-login-link"
              >
                <User size={16}/> Inloggen
              </Link>
            )}
            <Link
              to="/join"
              className="hidden md:inline-flex btn"
              data-testid="nav-book-trial-btn"
            >
              {t("nav.book")}
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
            <button
              onClick={() => setOpen(true)}
              className="lg:hidden w-11 h-11 flex items-center justify-center border border-[#0A0A0A] text-[#0A0A0A]"
              aria-label="Open menu"
              data-testid="nav-menu-open"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] bg-[#0A0A0A] text-[#F5F3F0] grain grain-dark"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-5 line-b line-dark">
              <span className="font-logo text-3xl">JOH.</span>
              <div className="flex items-center gap-4">
                <LangToggle dark />
                <button
                  onClick={() => setOpen(false)}
                  className="w-11 h-11 flex items-center justify-center border border-[#F5F3F0]"
                  aria-label="Close menu"
                  data-testid="nav-menu-close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <nav className="px-6 py-10 flex flex-col gap-6">
              {KEYS.map((n, i) => (
                <motion.button
                  key={n.key}
                  initial={{ y: 40, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 + i * 0.05, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => scrollTo(n.to)}
                  className="font-display text-5xl text-left uppercase"
                  data-testid={`mobile-nav-${n.key}`}
                >
                  {t(n.label)}
                </motion.button>
              ))}
              <motion.div
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                <Link to="/join" onClick={() => setOpen(false)} className="mt-4 btn-light w-fit inline-flex" data-testid="mobile-nav-book-trial">
                  {t("nav.book")} <ArrowUpRight size={14} />
                </Link>
              </motion.div>
              <motion.div
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.55, duration: 0.6 }}
                className="mt-2"
              >
                {user && user !== false ? (
                  <div className="flex flex-col gap-3">
                    <Link to="/dashboard" onClick={() => setOpen(false)} className="chip text-[#F5F3F0] inline-flex items-center gap-2" data-testid="mobile-nav-dashboard">
                      <User size={14}/> Dashboard — {user.first_name || (user.name || "").split(" ")[0]}
                    </Link>
                    <button onClick={() => { signOut(); setOpen(false); }} className="chip text-[#A3A3A3] inline-flex items-center gap-2 self-start" data-testid="mobile-nav-sign-out">
                      <LogOut size={14}/> Uitloggen
                    </button>
                  </div>
                ) : (
                  <Link to="/login" onClick={() => setOpen(false)} className="chip-login text-[#F5F3F0] inline-flex items-center gap-2" data-testid="mobile-nav-login">
                    <User size={16}/> Inloggen
                  </Link>
                )}
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
